import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "api/apiClient.ts";

// Define Category Type
interface Category {
  _id: string;
  name: string;
}

// Fetch categories function
const fetchCategories = async (): Promise<Category[]> => {
  const SAVED_LANG = localStorage.getItem("lang") || "en";

  const { data } = await apiClient.get("/categories", {
    headers: { lan: SAVED_LANG },
  });

  if (data.status !== "success" || !Array.isArray(data.data)) {
    throw new Error("Invalid response format");
  }
  
  return data.data;
};

// Hook to get categories
export const useCategories = () => {
  return useQuery({
    queryKey: ["categories"],
    queryFn: fetchCategories,
  });
};
export const useRefetchCategories = () => {
  const queryClient = useQueryClient();
  
  return {
    refetch: async () => {
      await queryClient.invalidateQueries({ queryKey: ["categories"] });
    }
  };
};

// Add Category Hook
export const useAddCategory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (category: Category) => {
      await apiClient.post("/categories/create", category);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categories"] });
    },
  });
};
// Delete Category Hook
export const useDeleteCategory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/categories/delete/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categories"] });
    },
  });
};

// Hook for Updating a Category
export const useUpdateCategory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (category: Category) => {
      return await apiClient.patch(`/categories/update/${category._id}`, category);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categorys"] });
    },
  });
};
